// socorroCheckout.js — o caminho de saída quando o pagamento falha.
//
// POR QUE ESTE ARQUIVO EXISTE:
// até aqui, um erro no checkout terminava numa mensagem vermelha e num cliente
// sozinho. Quem quisesse resolver tinha de achar o WhatsApp da editora no
// rodapé, abrir uma conversa e explicar do zero o que tinha acontecido — e a
// maioria simplesmente fecha a aba. A venda morre sem ninguém saber que ela
// existiu.
//
// A MENSAGEM VAI PRONTA, e é isso que muda o resultado. O cliente não sabe
// dizer "erro 422 na criação do pedido"; ele diz "não deu certo". Com o número
// do pedido, o valor e o texto do erro já escritos, quem atende resolve na
// primeira resposta em vez de puxar três mensagens de contexto.
//
// O QUE NÃO ENTRA NA MENSAGEM, de propósito: nada que o cliente não deva ver
// nem repetir. Nem token, nem id de sessão, nem resposta crua do gateway. O que
// vai é o que ele mesmo já tem na tela.

import { WHATSAPP_HREF, TELEFONE_E164 } from "../config/contato";

/** "R$ 1.234,56" */
const fmtBRL = (v) =>
  "R$ " + (Number(v) || 0).toFixed(2).replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, ".");

/**
 * O texto que o cliente manda para a editora quando o pagamento falha.
 *
 * @param {object} p
 * @param {string} [p.erro]      a mensagem que apareceu na tela
 * @param {string} [p.etapa]     onde parou ("pagamento", "pix", "pedido")
 * @param {number|string} [p.pedidoId] se o pedido chegou a existir
 * @param {number} [p.total]
 * @param {string} [p.nome]
 * @returns {string}
 */
export function mensagemDeSocorro({ erro, etapa, pedidoId, total, nome } = {}) {
  const linhas = [
    "Olá! Tentei finalizar uma compra no site da Insigne Acadêmica e não consegui.",
    "",
  ];

  if (nome) linhas.push(`Meu nome: ${nome}`);
  if (pedidoId) linhas.push(`Pedido nº ${pedidoId}`);
  if (total > 0) linhas.push(`Valor: ${fmtBRL(total)}`);

  // A etapa é o que separa "não consegui pagar" de "paguei e não recebi" —
  // duas conversas completamente diferentes para quem atende.
  if (etapa) linhas.push(`Onde parou: ${etapa}`);

  if (erro) {
    linhas.push("", "Mensagem que apareceu:", erro);
  }

  linhas.push("", "Podem me ajudar a concluir?");
  return linhas.join("\n");
}

/**
 * O link do WhatsApp da editora com a mensagem já preenchida.
 *
 * Usa `api.whatsapp.com/send`, e não `wa.me`: é a forma que o dono pediu, e ela
 * lida melhor com o desktop — cai no WhatsApp Web em vez de numa página
 * intermediária. O número é o PÚBLICO de atendimento (`TELEFONE_E164`), que é
 * quem fala com cliente; o número que recebe aviso de venda é outro e não
 * atende ninguém.
 *
 * `encodeURIComponent` é obrigatório: o texto leva quebras de linha, acentos e
 * cifrões, e sem escapar a mensagem chega cortada.
 */
export function linkSocorroWhatsApp(dados) {
  const texto = mensagemDeSocorro(dados);
  return `https://api.whatsapp.com/send/?phone=${TELEFONE_E164}`
    + `&text=${encodeURIComponent(texto)}&type=phone_number&app_absent=0`;
}

/** O link sem mensagem, para quando não há contexto nenhum a passar. */
export const WHATSAPP_ATENDIMENTO = WHATSAPP_HREF;

export default linkSocorroWhatsApp;
